import { useEffect, useState } from 'react';

export interface SectionNavItem {
  id: string;
  title: string;
  completed: number;
  total: number;
}

interface Props {
  sections: SectionNavItem[];
  activeId?: string;
  onJump: (id: string) => void;
}

/**
 * Sticky in-page nav for a long single-scroll form. Highlights the
 * currently-in-view section based on scroll position and shows per-section
 * progress (filled / total). Click a row to scroll to it.
 */
export function SectionNav({ sections, activeId, onJump }: Props) {
  return (
    <nav data-testid="form-section-nav" className="sticky top-4 hidden w-56 shrink-0 lg:block">
      <ul className="space-y-1 border-l border-gray-200 pl-4">
        {sections.map((s) => {
          const isActive = s.id === activeId;
          return (
            <li key={s.id}>
              <button
                type="button"
                data-testid={`form-section-nav-${s.id}`}
                onClick={() => onJump(s.id)}
                className={
                  'w-full rounded-md px-2 py-1 text-left text-sm transition ' +
                  (isActive
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50')
                }
              >
                <span className="block truncate">{s.title || 'Section'}</span>
                <span className="text-xs text-gray-400">
                  {s.completed}/{s.total}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Hook returning the id of the section currently most visible in the
 * viewport. Uses IntersectionObserver against DOM anchors `#form-section-<id>`.
 */
export function useActiveSectionId(sectionIds: string[]): string | undefined {
  const [active, setActive] = useState<string | undefined>(sectionIds[0]);
  useEffect(() => {
    if (sectionIds.length === 0) return;
    const observers: IntersectionObserver[] = [];
    const visibility = new Map<string, number>();
    for (const id of sectionIds) {
      const el = document.getElementById(`form-section-${id}`);
      if (!el) continue;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            visibility.set(id, entry.intersectionRatio);
          }
          let bestId: string | undefined;
          let bestRatio = -1;
          for (const [k, v] of visibility.entries()) {
            if (v > bestRatio) {
              bestRatio = v;
              bestId = k;
            }
          }
          if (bestRatio > 0 && bestId) setActive(bestId);
        },
        { threshold: [0, 0.25, 0.5, 0.75, 1] }
      );
      observer.observe(el);
      observers.push(observer);
    }
    return () => observers.forEach((o) => o.disconnect());
  }, [sectionIds.join('|')]);
  return active;
}
